import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Button,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Helper function to get user features from localStorage
const getUserFeaturesFromLocalStorage = () => {
  try {
    const userState = localStorage.getItem("userState");
    if (userState) {
      const parsedUserState = JSON.parse(userState);
      return parsedUserState.features || [];
    }
    return [];
  } catch (error) {
    console.error("Error parsing userState from localStorage:", error);
    return [];
  }
};

// Component that renders a module with title and checkable contents
const RoleAssignModule = ({
  title,
  contents,
  selectedFeatures,
  onFeatureToggle,
}) => {
  const { t } = useTranslation();

  // Check if all features in this module are selected
  const allSelected = contents.every((feature) =>
    selectedFeatures.some((f) => f.id === feature.id)
  );

  // Check if some (but not all) features in this module are selected
  const someSelected =
    contents.some((feature) =>
      selectedFeatures.some((f) => f.id === feature.id)
    ) && !allSelected;

  // Handle selecting/deselecting all features in this module
  const handleModuleToggle = () => {
    if (allSelected || someSelected) {
      // Deselect all features in this module
      contents.forEach((feature) => {
        if (selectedFeatures.some((f) => f.id === feature.id)) {
          onFeatureToggle(feature.id);
        }
      });
    } else {
      // Select all features in this module
      contents.forEach((feature) => {
        if (!selectedFeatures.some((f) => f.id === feature.id)) {
          onFeatureToggle(feature.id);
        }
      });
    }
  };

  return (
    <Box
      elevation={1}
      sx={{
        height: "100%",
        width: "100%",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Module Header */}
      <Box
        sx={{
          display: "flex",
          height: "47px",
          alignItems: "center",
          backgroundColor: "#B7BFCC",
          gap: "24px",
          borderRadius: "8px",
          padding: " 12px 12px",
          p: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              indeterminate={someSelected}
              checked={allSelected}
              onChange={handleModuleToggle}
              sx={{
                color: "rgba(65, 73, 88, 1)",
                "&.Mui-checked": {
                  color: "#20A8E0",
                },
                "&.MuiCheckbox-indeterminate": {
                  color: "#20A8E0",
                },
              }}
            />
          }
          label={
            <Typography sx={{ fontWeight: "bold", fontSize: "20px" }}>
              {title}
              {/* ({contents.length}) */}
            </Typography>
          }
        />
      </Box>

      {/* Module Content - Features */}
      <Box sx={{ p: 2, flexGrow: 1, overflowY: "auto", maxHeight: "auto" }}>
        {contents.map((feature) => (
          <FormControlLabel
            key={feature.id}
            control={
              <Checkbox
                checked={selectedFeatures.some((f) => f.id === feature.id)}
                onChange={() => onFeatureToggle(feature.id)}
                sx={{
                  color: "rgba(65, 73, 88, 1)",
                  "&.Mui-checked": {
                    color: "#20A8E0",
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{feature.name}</Typography>
              </Box>
            }
            sx={{
              display: "flex",
              mb: 1,
              alignItems: "center",
              "& .MuiFormControlLabel-label": {
                mt: 0,
              },
            }}
          />
        ))}

        {contents.length === 0 && (
          <Typography
            color="textSecondary"
            variant="body2"
            sx={{ py: 2, textAlign: "center" }}
          >
            {t("No features available in this category")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Main component that handles role assignment
const RoleAssignCheckbox = ({
  getAllTheAvailableFeatures,
  selectedRoleData,
  assignFeatureToTheRole,
  isSubmitting,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [features, setFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedModule, setSelectedModule] = useState("all");

  // Function to extract module name from feature data
  const getModuleFromFeature = (feature) => {
    // Extract module name from displayName or name
    const nameParts = feature.displayName ? feature.displayName.split("-") : [];
    if (nameParts.length > 0) {
      return nameParts[0];
    }

    // If no module found in displayName, try to extract from name
    if (feature.name.includes("User")) {
      return "User Management";
    } else if (feature.name.includes("Vehicle")) {
      return "Vehicle Management";
    } else if (feature.name.includes("Route")) {
      return "Route Management";
    } else if (feature.name.includes("Stop")) {
      return "Stop Management";
    } else if (feature.name.includes("Schedule")) {
      return "Schedule Management";
    } else if (feature.name.includes("Role")) {
      return "Role Management";
    } else if (feature.name.includes("Alert")) {
      return "Alert & Notification";
    } else if (feature.name.includes("Driver")) {
      return "Driver Management";
    } else if (feature.name.includes("Site")) {
      return "Site Management";
    } else if (feature.name.includes("Organization")) {
      return "Organization Management";
    } else if (feature.name.includes("Data")) {
      return "Data Management";
    }

    return "Other";
  };

  // Fetch features and organize them into modules
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        setLoading(true);

        // Get user's available features from localStorage
        const userAvailableFeatures = getUserFeaturesFromLocalStorage();
        console.log(
          "User's available features from localStorage:",
          userAvailableFeatures
        );

        // Get all features from the API
        const allFeaturesData = await getAllTheAvailableFeatures();
        console.log("All Features Data from API:", allFeaturesData);

        // Filter features to only include those available to the user
        // Match by name, displayName, or api_path + action combination
        const filteredFeatures = allFeaturesData.filter((apiFeature) => {
          return userAvailableFeatures.some((userFeature) => {
            // Match by name
            if (apiFeature.name === userFeature.name) {
              return true;
            }

            // Match by displayName
            if (apiFeature.displayName === userFeature.displayName) {
              return true;
            }

            // Match by API path and action combination
            if (
              apiFeature.api_path === userFeature.api_path &&
              apiFeature.action === userFeature.action
            ) {
              return true;
            }

            return false;
          });
        });

        console.log("Filtered features (available to user):", filteredFeatures);

        // Group features by module
        const moduleMap = {};
        filteredFeatures.forEach((feature) => {
          const moduleName = getModuleFromFeature(feature);

          if (!moduleMap[moduleName]) {
            moduleMap[moduleName] = [];
          }

          moduleMap[moduleName].push(feature);
        });

        // Title mapping
        const titleMapping = {
          User: "User Management",
          Vehicle: "Vehicle Management",
          Route: "Route Management",
          Stop: "Stop Management",
          Schedule: "Schedule Management",
          Role: "Role Management",
          Alert: "Alert & Notification",
          Driver: "Driver Management",
          Site: "Site Management",
          Organization: "Organization Management",
          Data: "Data Management",
          Other: "Other Management",
        };

        // Convert moduleMap to array format
        const moduleArray = Object.entries(moduleMap)
          .map(([title, contents]) => ({
            title: titleMapping[title] || title,
            contents,
          }))
          .sort((a, b) => a.title.localeCompare(b.title));

        setModules(moduleArray);
        setFeatures(filteredFeatures);

        // Set initially selected features based on selectedRoleData
        if (selectedRoleData?.features) {
          const initialSelectedFeatures = filteredFeatures.filter((feature) =>
            selectedRoleData.features.some(
              (f) => f.id === feature.id || f.feature?.id === feature.id
            )
          );
          setSelectedFeatures(initialSelectedFeatures);
        }
      } catch (error) {
        console.error("Error fetching features:", error);
        setError("Failed to load features. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, [getAllTheAvailableFeatures, selectedRoleData]);

  // Handle checkbox change
  const handleFeatureToggle = (featureId) => {
    setSelectedFeatures((prevSelected) => {
      const isCurrentlySelected = prevSelected.some(
        (feature) => feature.id === featureId
      );
      const targetFeature = features.find(
        (feature) => feature.id === featureId
      );

      if (isCurrentlySelected) {
        // Remove the feature
        return prevSelected.filter((feature) => feature.id !== featureId);
      } else {
        // Add the feature
        return [...prevSelected, targetFeature];
      }
    });
  };

  // Reset all checkboxes
  const resetAllCheckboxes = () => {
    setSelectedFeatures([]);
  };

  // Submit selected features
  const assignFeatures = useCallback(async () => {
    try {

      setError(null);

      // Get IDs of selected features
      const selectedFeatureIds = selectedFeatures.map((feature) => feature.id);
      const roleId = selectedRoleData.id;

      // Call the API to assign features to the role
      await assignFeatureToTheRole(roleId, selectedFeatureIds);

      // Navigate back to the roles page
      navigate("/user/role");
    } catch (error) {
      console.error("Error assigning features to role:", error);
      setError("Failed to assign features. Please try again.");
    }
  }, [selectedFeatures, selectedRoleData, assignFeatureToTheRole, navigate]);

  // Handle search change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle module filter change
  const handleModuleChange = (event) => {
    setSelectedModule(event.target.value);
  };

  // Get unique module names for the dropdown
  const getModuleOptions = () => {
    return [
      { value: "all", label: "All Modules" },
      ...modules.map((module) => ({
        value: module.title,
        label: module.title,
      })),
    ];
  };

  // Filter modules based on search and selected module
  const getFilteredModules = () => {
    return modules.filter((module) => {
      // Filter by selected module
      if (selectedModule !== "all" && module.title !== selectedModule) {
        return false;
      }

      // Filter by search term
      if (searchTerm && searchTerm.trim() !== "") {
        const lowerSearchTerm = searchTerm.toLowerCase();

        // Check if module title matches
        if (module.title.toLowerCase().includes(lowerSearchTerm)) {
          return true;
        }

        // Check if any feature in the module matches
        return module.contents.some(
          (feature) =>
            feature.name.toLowerCase().includes(lowerSearchTerm) ||
            (feature.description &&
              feature.description.toLowerCase().includes(lowerSearchTerm))
        );
      }

      return true;
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography>{t("Loading features...")}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Box
          sx={{
            color: "error.main",
            mb: 2,
            p: 2,
            bgcolor: "error.light",
            borderRadius: 1,
          }}
        >
          {error}
        </Box>
      )}

      {/* Search and Filter Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <TextField
          placeholder="Search"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "320px",
            height: 35,
            borderRadius: "4px",
            border: "1px solid #414958",
            background: "#F2F2F3",
            "& .MuiOutlinedInput-root": {
              height: 35,
            },
          }}
        />

        <FormControl
          sx={{
            width: "202px",
            height: 35,
            "& .MuiOutlinedInput-root": {
              height: 35,
              borderRadius: "4px",
              border: "1px solid #414958",
              background: "#F2F2F3",
            },
            "& .MuiInputLabel-root": {
              lineHeight: "1.4em",
            },
          }}
        >
          {/* <InputLabel id="module-select-label">{t("Module")}</InputLabel> */}
          <Select
            labelId="module-select-label"
            id="module-select"
            value={selectedModule}
            label={t("Module")}
            onChange={handleModuleChange}
          >
            {getModuleOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Feature modules - 3 per row */}
      <Grid container spacing={3} sx={{ mb: 4, mt: 1 }}>
        {getFilteredModules().map((module, index) => (
          <Grid item xs={12} md={4} key={index} sx={{ display: "flex" }}>
            <RoleAssignModule
              title={module.title}
              contents={module.contents}
              selectedFeatures={selectedFeatures}
              onFeatureToggle={handleFeatureToggle}
            />
          </Grid>
        ))}

        {getFilteredModules().length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="textSecondary">
                {t("No modules match your search criteria")}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Action buttons */}
      <Box
        sx={{
          width: "100%",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "24px",
          display: "flex",
          flexDirection: "row",
          gap: "64px",
          paddingBottom: "10px",
        }}
      >
        <Button
          color="primary"
          onClick={resetAllCheckboxes}
          disabled={loading}
          sx={{
            width: "125px",
            height: "55px",
            borderRadius: "8px",
            borderWidth: 1,
            background: "#F2F2F3",
            border: "1px solid #414958",
            color: "#1F242E",
            textTransform: "none",
            "&:hover": {
              background: "#E0E0E1",
            },
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          {t("Cancel")}
        </Button>

        <Button
          variant="contained"
          onClick={assignFeatures}
          disabled={loading}
          sx={{
            width: "125px",
            height: "55px",
            borderRadius: "8px",
            borderWidth: 1,
            background: "#F9E719",
            border: "1px solid #414958",
            color: "#1F242E",
            textTransform: "none",
            "&:hover": {
              background: "#F9E719",
            },
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          {isSubmitting ? <CircularProgress /> : t("Save")}
        </Button>
      </Box>
    </Box>
  );
};

export default RoleAssignCheckbox;
